import React from "react"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useTypedFeatureFlag } from "@/hooks/use-feature-flag"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@ui/sidebar"
import {
  AudioWaveform,
  BookOpen,
  Bot,
  Calendar,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  Clock,
  Command,
  CreditCard,
  FolderOpen,
  GalleryVerticalEnd,
  Kanban,
  LayoutDashboard,
  LineChart,
  Link2,
  Megaphone,
  MessageSquare,
  Puzzle,
  Share2,
  Users,
  Users2,
  Zap,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"

import { CreditUsageCard } from "../../../features/billing/components/credit-usage-card"
import { NavUser } from "./nav-user"

export function AppSidebar() {
  const location = useLocation()
  const { user } = useAuth()

  const broadcastEnabled = useTypedFeatureFlag("broadcast_enabled", false)
  const leadsPipelineEnabled = useTypedFeatureFlag("leads_pipeline_enabled", false)
  const automationsEnabled = useTypedFeatureFlag("automations_enabled", false)
  const groupsEnabled = useTypedFeatureFlag("groups_enabled", false)
  const mediaLibraryEnabled = useTypedFeatureFlag("media_library_enabled", false)
  const qualificationFormsEnabled = useTypedFeatureFlag("qualification_forms_enabled", false)
  const pluginsEnabled = useTypedFeatureFlag("plugins_enabled", false)
  const paymentLinksEnabled = useTypedFeatureFlag("payment_links_enabled", false)
  const schedulersEnabled = useTypedFeatureFlag("schedulers_enabled", false)
  const commerceModuleEnabled = useTypedFeatureFlag("commerce_module_enabled", false)

  // Staff users only see Conversas, Agenda, and CRM
  const isStaffOnly = user?.roles?.includes("staff") && !user?.roles?.includes("client")

  const allItems = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
      staffVisible: false,
      get isActive() {
        return location.pathname === "/dashboard"
      },
    },
    {
      title: "Agentes",
      url: "/agents",
      icon: Bot,
      staffVisible: false,
      get isActive() {
        return location.pathname.startsWith("/agents")
      },
    },
    {
      title: "Agenda",
      icon: Calendar,
      staffVisible: true,
      get isActive() {
        return ["/calendar", "/availability"].includes(location.pathname)
      },
      subItems: [
        {
          title: "Agendamentos",
          url: "/calendar",
          icon: Calendar,
          get isActive() {
            return location.pathname === "/calendar"
          },
        },
        {
          title: "Disponibilidade",
          url: "/availability",
          icon: Clock,
          get isActive() {
            return location.pathname === "/availability"
          },
        },
      ],
    },
    {
      title: "Contatos",
      url: "/contacts",
      icon: Users,
      staffVisible: false,
      get isActive() {
        return location.pathname.startsWith("/contacts")
      },
    },
    {
      title: "Relatórios",
      url: "/reports",
      icon: LineChart,
      staffVisible: false,
      get isActive() {
        return location.pathname.startsWith("/reports")
      },
    },
    {
      title: "Conversas",
      url: "/chat",
      icon: MessageSquare,
      staffVisible: true,
      get isActive() {
        return location.pathname.startsWith("/chat")
      },
    },
    ...(groupsEnabled
      ? [
          isStaffOnly
            ? {
                title: "Grupos",
                url: "/groups",
                icon: Users2,
                staffVisible: false,
                get isActive() {
                  return location.pathname.startsWith("/groups")
                },
              }
            : {
                title: "Gestão de grupos",
                icon: Users2,
                staffVisible: false,
                get isActive() {
                  return location.pathname.startsWith("/groups")
                },
                subItems: [
                  {
                    title: "Grupos",
                    url: "/groups",
                    get isActive() {
                      const path = location.pathname
                      return (
                        path === "/groups" ||
                        (path.startsWith("/groups/") &&
                          !["/groups/team", "/groups/roles", "/groups/categories"].includes(path))
                      )
                    },
                  },
                  {
                    title: "Equipe",
                    url: "/groups/team",
                    get isActive() {
                      return location.pathname === "/groups/team"
                    },
                  },
                  {
                    title: "Papéis",
                    url: "/groups/roles",
                    get isActive() {
                      return location.pathname === "/groups/roles"
                    },
                  },
                  {
                    title: "Categorias",
                    url: "/groups/categories",
                    get isActive() {
                      return location.pathname === "/groups/categories"
                    },
                  },
                ],
              },
        ]
      : []),
    ...(leadsPipelineEnabled
      ? [
          {
            title: "CRM",
            url: "/leads",
            icon: Kanban,
            staffVisible: true,
            get isActive() {
              return location.pathname.startsWith("/leads")
            },
          },
        ]
      : []),
    ...(automationsEnabled
      ? [
          {
            title: "Automações",
            url: "/automations",
            icon: Zap,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/automations")
            },
          },
        ]
      : []),
    ...(commerceModuleEnabled
      ? [
          {
            title: "Comercial",
            url: "/comercial/catalogos",
            icon: ClipboardList,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/comercial")
            },
            subItems: [
              {
                title: "Catálogos",
                url: "/comercial/catalogos",
                icon: BookOpen,
                get isActive() {
                  return location.pathname === "/comercial/catalogos"
                },
              },
              {
                title: "Pedidos",
                url: "/comercial/pedidos",
                icon: ClipboardList,
                get isActive() {
                  return location.pathname === "/comercial/pedidos"
                },
              },
            ],
          },
        ]
      : []),
    ...(broadcastEnabled
      ? [
          {
            title: "Disparos",
            url: "/broadcast",
            icon: Megaphone,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/broadcast")
            },
          },
        ]
      : []),
    {
      title: "Canais",
      url: "/settings/channels",
      icon: Share2,
      staffVisible: false,
      get isActive() {
        return location.pathname === "/settings/channels"
      },
    },
    {
      title: "Integrações",
      url: "/integrations",
      icon: Link2,
      staffVisible: false,
      get isActive() {
        return location.pathname.startsWith("/integrations")
      },
    },
    ...(mediaLibraryEnabled
      ? [
          {
            title: "Biblioteca de Mídia",
            url: "/media-library",
            icon: FolderOpen,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/media-library")
            },
          },
        ]
      : []),
    ...(qualificationFormsEnabled
      ? [
          {
            title: "Qualificação de Leads",
            url: "/qualification-forms",
            icon: ClipboardList,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/qualification-forms")
            },
          },
        ]
      : []),
    ...(pluginsEnabled
      ? [
          {
            title: "Plugins",
            url: "/plugins",
            icon: Puzzle,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/plugins")
            },
          },
        ]
      : []),
    ...(paymentLinksEnabled
      ? [
          {
            title: "Cobranças",
            url: "/payment-links",
            icon: CreditCard,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/payment-links")
            },
          },
        ]
      : []),
    ...(schedulersEnabled
      ? [
          {
            title: "Mensagens Agendadas",
            url: "/schedulers",
            icon: CalendarClock,
            staffVisible: false,
            get isActive() {
              return location.pathname.startsWith("/schedulers")
            },
          },
        ]
      : []),
  ]

  const visibleItems = isStaffOnly ? allItems.filter(item => item.staffVisible) : allItems

  const navData = [
    {
      group: "Principal",
      showGroupLabel: false,
      items: visibleItems,
    },
    /*{
      group: "Ajuda",
      showGroupLabel: true,
      items: [
        {
          title: "Suporte",
          url: "/suporte",
          icon: LifeBuoy,
          get isActive() {
            return location.pathname === "/suporte"
          },
        },
        {
          title: "Feedback",
          url: "/feedback",
          icon: MessageCircle,
          get isActive() {
            return location.pathname === "/feedback"
          },
        },
      ],
    },*/
  ]

  const data = {
    user: {
      name: user?.name || "",
      email: user?.email || "",
      avatar: "/avatars/shadcn.jpg",
    },
    teams: [
      {
        name: "Acme Inc",
        logo: GalleryVerticalEnd,
        plan: "Enterprise",
      },
      {
        name: "Acme Corp.",
        logo: AudioWaveform,
        plan: "Startup",
      },
      {
        name: "Evil Corp.",
        logo: Command,
        plan: "Free",
      },
    ],
    nav: navData,
  }

  return (
    <Sidebar variant="inset">
      <SidebarHeader className="flex justify-start px-6 py-3">
        <img src="/front-end/speeke2.svg" alt="Speeke" className="h-8 w-30" />
      </SidebarHeader>

      <SidebarContent>
        {data.nav.map(navGroup => (
          <SidebarGroup key={navGroup.group}>
            {navGroup.showGroupLabel && <SidebarGroupLabel>{navGroup.group}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {navGroup.items?.map(item => (
                  <React.Fragment key={item.title}>
                    {(item.subItems || []).length == 0 ? (
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={item.isActive}>
                          <Link to={item.url || "#"}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ) : (
                      <Collapsible
                        asChild
                        defaultOpen={item.isActive}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton tooltip={item.title}>
                              {item.icon && <item.icon />}
                              <span>{item.title}</span>
                              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.subItems?.map(subItem => (
                                <SidebarMenuSubItem key={subItem.title}>
                                  <SidebarMenuSubButton asChild isActive={subItem.isActive}>
                                    <Link to={subItem.url}>
                                      <span>{subItem.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    )}
                  </React.Fragment>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {!isStaffOnly && (
          <div className="p-1">
            <CreditUsageCard />
          </div>
        )}

        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  )
}
